/**
 * Framework-agnostic state machine behind `useConfirm` (audit MAINT-6).
 *
 * It owns a single in-flight confirmation: `request()` opens the dialog and
 * returns a promise that settles `true` (confirmed) or `false` (cancelled).
 * Keeping this out of React makes the promise semantics — one-shot settle,
 * superseding requests — directly unit-testable without a renderer.
 */
import type { ReactNode } from 'react';

export interface ConfirmOptions {
  title: string;
  /** Body copy. Plain string or any node. */
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Render the confirm button as destructive (red). */
  destructive?: boolean;
}

export interface ConfirmState {
  open: boolean;
  options: ConfirmOptions | null;
}

export interface ConfirmController {
  /** Open the dialog; resolves true if confirmed, false if cancelled. */
  request: (options: ConfirmOptions) => Promise<boolean>;
  /** Settle the in-flight request. No-op if nothing is pending (one-shot). */
  settle: (value: boolean) => void;
  getState: () => ConfirmState;
  subscribe: (listener: () => void) => () => void;
}

const CLOSED: ConfirmState = { open: false, options: null };

export function createConfirmController(): ConfirmController {
  let state: ConfirmState = CLOSED;
  let pending: ((value: boolean) => void) | null = null;
  const listeners = new Set<() => void>();

  const emit = (): void => {
    for (const listener of listeners) listener();
  };

  return {
    request(options) {
      // A new request while one is open supersedes it: settle the old false.
      if (pending) {
        const stale = pending;
        pending = null;
        stale(false);
      }
      state = { open: true, options };
      emit();
      return new Promise<boolean>((resolve) => {
        pending = resolve;
      });
    },
    settle(value) {
      if (!pending) return; // one-shot — ignore repeat clicks / double-settle
      const resolve = pending;
      pending = null;
      state = CLOSED;
      emit();
      resolve(value);
    },
    getState() {
      return state;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
